(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-ah-ar-root]';
  const initialized = new WeakSet();
  const MV_FALLBACK_SRC = 'https://cdn.jsdelivr.net/npm/@google/model-viewer@4.3.1/dist/model-viewer.min.js';
  const QR_ENDPOINT = 'https://api.qrserver.com/v1/create-qr-code/?size=188x188&margin=0&data=';

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const normalize = (value = '') => String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

  const parseDimension = (value = '') => {
    const clean = String(value).replace(/,/g, '.');
    const match = clean.match(/(\d+(?:\.\d+)?)\s*(?:cm)?\s*[x×✕*]\s*(\d+(?:\.\d+)?)\s*(?:cm)?/i);
    if (!match) return null;
    const a = Number(match[1]);
    const b = Number(match[2]);
    if (!Number.isFinite(a) || !Number.isFinite(b) || a <= 0 || b <= 0) return null;
    return { widthCm: a, heightCm: b, label: `${a} × ${b} cm` };
  };

  const frameKey = (label = '') => {
    const v = normalize(label);
    if (!v || v.includes('sans cadre') || v.includes('sans-cadre') || v.includes('no frame') || v === 'sans' || v.includes('frameless')) return 'frameless';
    if (v.includes('beige') || v.includes('clair') || v.includes('cream') || v.includes('naturel') || v.includes('light wood')) return 'beige';
    if (v.includes('blanc') || v.includes('white')) return 'white';
    if (v.includes('noir') || v.includes('black')) return 'black';
    return 'black';
  };

  const findOptionIndex = (options, wantedName, fallbacks) => {
    const wanted = normalize(wantedName);
    let index = options.findIndex((name) => normalize(name) === wanted);
    if (index >= 0) return index;
    return options.findIndex((name) => fallbacks.some((needle) => normalize(name).includes(needle)));
  };

  const safeCssEscape = (value) => {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return String(value).replace(/["\\]/g, '\\$&');
  };

  const getVariantId = (root) => {
    const sectionId = root.dataset.sectionId;
    const section = document.getElementById(`MainProduct-${sectionId}`) || document;
    const escaped = safeCssEscape(sectionId);
    const form =
      section.querySelector(`form[id="product-form-${escaped}"]`) ||
      section.querySelector('form[action*="/cart/add"]');
    const input = form?.querySelector('input[name="id"]');

    if (input?.value) return Number(input.value);

    try {
      const value = new URL(window.location.href).searchParams.get('variant');
      return value ? Number(value) : null;
    } catch (_) {
      return null;
    }
  };

  const loadImageRatio = (url) => new Promise((resolve) => {
    if (!url) return resolve(null);
    const image = new Image();
    image.decoding = 'async';
    image.onload = () => resolve(
      image.naturalWidth && image.naturalHeight
        ? image.naturalWidth / image.naturalHeight
        : null
    );
    image.onerror = () => resolve(null);
    image.src = url;
  });

  const waitForModelViewer = async (timeoutMs = 3500) => {
    if (customElements.get('model-viewer')) return true;
    let done = false;
    customElements.whenDefined('model-viewer')
      .then(() => { done = true; })
      .catch(() => {});
    const started = Date.now();
    while (!done && Date.now() - started < timeoutMs) await sleep(80);
    return !!customElements.get('model-viewer');
  };

  const ensureModelViewer = async () => {
    if (await waitForModelViewer(2500)) return true;

    if (!document.querySelector('script[data-ah-model-viewer-fallback]')) {
      const script = document.createElement('script');
      script.type = 'module';
      script.src = MV_FALLBACK_SRC;
      script.dataset.ahModelViewerFallback = 'true';
      document.head.appendChild(script);
    }

    return waitForModelViewer(6000);
  };

  const isLikelyDesktop = () => {
    const mobileUa = /Android|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const finePointer = window.matchMedia?.('(pointer: fine)').matches;
    const hover = window.matchMedia?.('(hover: hover)').matches;
    return !mobileUa && (finePointer || hover) && window.innerWidth >= 820;
  };

  async function init(root) {
    if (!root || initialized.has(root)) return;
    initialized.add(root);

    const openButton = root.querySelector('[data-ah-ar-open]');
    const overlay = root.querySelector('[data-ah-ar-overlay]');
    const closeButtons = root.querySelectorAll('[data-ah-ar-close]');
    const viewer = root.querySelector('[data-ah-ar-viewer]');
    const loading = root.querySelector('[data-ah-ar-loading]');
    const loadingText = root.querySelector('[data-ah-ar-loading-text]');
    const errorBox = root.querySelector('[data-ah-ar-error]');
    const errorText = root.querySelector('[data-ah-ar-error-text]');
    const retryButton = root.querySelector('[data-ah-ar-retry]');
    const sizeOutput = root.querySelector('[data-ah-ar-size]');
    const frameOutput = root.querySelector('[data-ah-ar-frame]');
    const selectionOutput = root.querySelector('[data-ah-ar-selection]');
    const unsupported = root.querySelector('[data-ah-ar-unsupported]');
    const copyButton = root.querySelector('[data-ah-ar-copy-link]');
    const copyLabel = root.querySelector('[data-ah-ar-copy-label]');
    const variantsScript = root.querySelector('[data-ah-ar-variants]');
    const sizeChoices = root.querySelector('[data-ah-ar-size-choices]');
    const frameChoices = root.querySelector('[data-ah-ar-frame-choices]');
    const sizeGroup = root.querySelector('[data-ah-ar-size-group]');
    const frameGroup = root.querySelector('[data-ah-ar-frame-group]');
    const desktopTransfer = root.querySelector('[data-ah-ar-desktop]');
    const qrImage = root.querySelector('[data-ah-ar-qr]');
    const qrLoading = root.querySelector('[data-ah-ar-qr-loading]');
    const qrFallback = root.querySelector('[data-ah-ar-qr-fallback]');
    const mobileLink = root.querySelector('[data-ah-ar-mobile-link]');

    if (!openButton || !overlay || !viewer || !variantsScript) return;

    let data;
    try {
      data = JSON.parse(variantsScript.textContent || '{}');
    } catch (error) {
      console.error('[Ambient AR] Variants JSON invalide', error);
      return;
    }

    const options = Array.isArray(data.options) ? data.options : [];
    const variants = Array.isArray(data.variants) ? data.variants : [];
    const optionValues = Array.isArray(data.optionValues) ? data.optionValues : [];
    if (!variants.length) return;

    const sizeIndex = findOptionIndex(
      options,
      root.dataset.sizeOption || 'Taille',
      ['taille', 'size', 'dimension', 'format']
    );
    const frameIndex = findOptionIndex(
      options,
      root.dataset.frameOption || 'Cadre',
      ['cadre', 'frame', 'encadrement']
    );

    const models = {
      black: root.dataset.modelBlack || '',
      white: root.dataset.modelWhite || '',
      beige: root.dataset.modelBeige || '',
      frameless: root.dataset.modelFrameless || ''
    };

    const defaultWidth = Number(root.dataset.defaultWidth || 50);
    const defaultHeight = Number(root.dataset.defaultHeight || 70);
    const artworkUrl = root.dataset.artworkUrl || root.dataset.posterUrl || '';

    let lastModelUrl = '';
    let lastAppliedArtwork = '';
    let artworkRatio = null;
    let lastFocused = null;
    let textureToken = 0;
    let currentLoadToken = 0;
    let modelViewerReady = false;
    let previewVariant = null;
    let previewSizeValue = null;
    let previewFrameValue = null;
    let manualPreview = false;
    let lastStoreVariantId = null;
    let desktopMode = isLikelyDesktop();

    const setLoading = (show, message = 'Préparation de l’aperçu 3D…') => {
      if (loadingText) loadingText.textContent = message;
      if (loading) loading.hidden = !show;
      if (show && errorBox) errorBox.hidden = true;
    };

    const setError = (message, technical = '') => {
      if (loading) loading.hidden = true;
      if (errorText) errorText.textContent = message;
      if (errorBox) errorBox.hidden = false;
      if (technical) console.error('[Ambient AR]', technical);
    };

    const clearError = () => {
      if (errorBox) errorBox.hidden = true;
    };

    const chooseModel = (frameLabel) => {
      const key = frameKey(frameLabel);
      return models[key] || models.black || models.frameless || models.white || models.beige || '';
    };

    const orientDimensions = (dims) => {
      let widthCm = dims.widthCm;
      let heightCm = dims.heightCm;

      if (artworkRatio) {
        const artLandscape = artworkRatio > 1.08;
        const artPortrait = artworkRatio < 0.92;
        if (artLandscape && widthCm < heightCm) [widthCm, heightCm] = [heightCm, widthCm];
        if (artPortrait && widthCm > heightCm) [widthCm, heightCm] = [heightCm, widthCm];
      }

      return { widthCm, heightCm, label: `${widthCm} × ${heightCm} cm` };
    };

    const variantById = (id) => {
      if (!id) return null;
      return variants.find((v) => Number(v.id) === Number(id)) || null;
    };

    const storefrontVariant = () => variantById(getVariantId(root)) || variants[0];

    const activeVariant = () => previewVariant || storefrontVariant() || variants[0];

    const uniqueValues = (index, kind = '') => {
      if (index < 0) return [];
      const seen = new Set();
      const values = [];

      const optionName = options[index] || '';
      const declared = optionValues.find((item) => normalize(item?.name) === normalize(optionName));
      if (declared && Array.isArray(declared.values)) {
        declared.values.forEach((value) => {
          const text = String(value || '').trim();
          if (!text || seen.has(text)) return;
          seen.add(text);
          values.push(text);
        });
      }

      variants.forEach((variant) => {
        const value = Array.isArray(variant.options) ? variant.options[index] : null;
        const text = String(value || '').trim();
        if (!text || seen.has(text)) return;
        seen.add(text);
        values.push(text);
      });

      // Les 4 cadres AR sont toujours disponibles dans le visualiseur,
      // même si Shopify ne les renvoie pas tous dans product.variants.
      if (kind === 'frame') {
        ['Sans cadre', 'Cadre beige clair', 'Cadre noir', 'Cadre blanc'].forEach((value) => {
          if (!seen.has(value)) {
            seen.add(value);
            values.push(value);
          }
        });
      }

      return values;
    };

    const findVariantForChoice = (index, value, current) => {
      const currentOptions = Array.isArray(current?.options) ? [...current.options] : [];
      currentOptions[index] = value;

      const exact = variants.find((variant) => {
        const opts = Array.isArray(variant.options) ? variant.options : [];
        return currentOptions.length > 0 &&
          currentOptions.every((wanted, i) => wanted == null || opts[i] === wanted);
      });
      if (exact) return exact;

      if (sizeIndex >= 0 && frameIndex >= 0) {
        const wantedSize = index === sizeIndex
          ? value
          : (Array.isArray(current?.options) ? current.options[sizeIndex] : null);
        const wantedFrame = index === frameIndex
          ? value
          : (Array.isArray(current?.options) ? current.options[frameIndex] : null);

        const pair = variants.find((variant) => {
          const opts = Array.isArray(variant.options) ? variant.options : [];
          return (!wantedSize || opts[sizeIndex] === wantedSize) &&
            (!wantedFrame || opts[frameIndex] === wantedFrame);
        });
        if (pair) return pair;
      }

      return variants.find((variant) => {
        const opts = Array.isArray(variant.options) ? variant.options : [];
        return opts[index] === value;
      }) || current || variants[0];
    };

    const makeMobileUrl = (variant) => {
      try {
        const url = new URL(window.location.href);
        if (variant?.id) url.searchParams.set('variant', String(variant.id));
        if (previewSizeValue) url.searchParams.set('ah_ar_size', previewSizeValue);
        else url.searchParams.delete('ah_ar_size');
        if (previewFrameValue) url.searchParams.set('ah_ar_frame', previewFrameValue);
        else url.searchParams.delete('ah_ar_frame');
        return url.toString();
      } catch (_) {
        return window.location.href;
      }
    };

    const updateQr = (variant) => {
      const mobileUrl = makeMobileUrl(variant);
      if (mobileLink) mobileLink.href = mobileUrl;

      // On ne contacte le service QR que quand la fenêtre desktop est réellement ouverte.
      if (!qrImage || !desktopMode || overlay.hidden) return;

      if (qrLoading) qrLoading.hidden = false;
      if (qrFallback) qrFallback.hidden = true;
      qrImage.hidden = true;

      const src = `${QR_ENDPOINT}${encodeURIComponent(mobileUrl)}`;
      qrImage.onload = () => {
        qrImage.hidden = false;
        if (qrLoading) qrLoading.hidden = true;
        if (qrFallback) qrFallback.hidden = true;
      };
      qrImage.onerror = () => {
        qrImage.hidden = true;
        if (qrLoading) qrLoading.hidden = true;
        if (qrFallback) qrFallback.hidden = false;
      };
      qrImage.src = src;
    };

    const renderChoiceButtons = (container, group, index, currentValue, kind) => {
      if (!container || !group) return;
      if (index < 0) {
        group.hidden = true;
        return;
      }

      group.hidden = false;
      container.innerHTML = '';

      uniqueValues(index, kind).forEach((value) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ah-ar__choice';
        button.textContent = value;
        button.dataset.ahArChoice = kind;
        button.dataset.ahArValue = value;
        if (value === currentValue) {
          button.classList.add('is-active');
          button.setAttribute('aria-pressed', 'true');
        } else {
          button.setAttribute('aria-pressed', 'false');
        }
        container.appendChild(button);
      });
    };

    const renderConfiguration = (variant) => {
      const variantOptions = Array.isArray(variant?.options) ? variant.options : [];
      const rawSize = sizeIndex >= 0
        ? (previewSizeValue || variantOptions[sizeIndex] || '')
        : '';
      const frameLabel = frameIndex >= 0
        ? (previewFrameValue || variantOptions[frameIndex] || '')
        : '';

      const parsed = parseDimension(rawSize) || {
        widthCm: defaultWidth,
        heightCm: defaultHeight,
        label: `${defaultWidth} × ${defaultHeight} cm`
      };
      const dims = orientDimensions(parsed);

      if (sizeOutput) sizeOutput.textContent = dims.label;
      if (frameOutput) frameOutput.textContent = frameLabel || '—';
      if (selectionOutput) {
        selectionOutput.textContent = `${dims.label}${frameLabel ? ` · ${frameLabel}` : ''}`;
      }

      renderChoiceButtons(sizeChoices, sizeGroup, sizeIndex, rawSize, 'size');
      renderChoiceButtons(frameChoices, frameGroup, frameIndex, frameLabel, 'frame');
      updateQr(variant);

      return { dims, frameLabel };
    };

    const applyArtworkTexture = async () => {
      if (!artworkUrl || !viewer.model || typeof viewer.createTexture !== 'function') return;
      const token = ++textureToken;

      try {
        const material =
          viewer.model.getMaterialByName?.('Artwork') ||
          viewer.model.materials?.find((m) => m.name === 'Artwork');

        if (!material?.pbrMetallicRoughness?.baseColorTexture) {
          console.warn('[Ambient AR] Matériau Artwork introuvable.');
          return;
        }

        const texture = await viewer.createTexture(artworkUrl);
        if (token !== textureToken) return;

        material.pbrMetallicRoughness.baseColorTexture.setTexture(texture);
        material.pbrMetallicRoughness.setBaseColorFactor([1, 1, 1, 1]);
        lastAppliedArtwork = artworkUrl;

        if (viewer.updateComplete) {
          try { await viewer.updateComplete; } catch (_) {}
        }
      } catch (error) {
        console.warn('[Ambient AR] Image du tableau non appliquée au modèle 3D.', error);
      }
    };

    const loadModel = async (modelUrl, reason = 'initial') => {
      if (!modelUrl) {
        setError('Le modèle 3D est introuvable.', 'Aucune URL de modèle disponible.');
        return false;
      }

      const token = ++currentLoadToken;
      clearError();
      setLoading(true, 'Préparation de l’aperçu 3D…');

      const result = await new Promise((resolve) => {
        let finished = false;
        let timeoutId;

        const cleanup = () => {
          clearTimeout(timeoutId);
          viewer.removeEventListener('load', onLoad);
          viewer.removeEventListener('error', onError);
        };

        const done = (ok, detail = '') => {
          if (finished) return;
          finished = true;
          cleanup();
          resolve({ ok, detail });
        };

        const onLoad = () => done(true);
        const onError = (event) =>
          done(false, event?.detail?.message || event?.detail || 'model-viewer error');

        viewer.addEventListener('load', onLoad, { once: true });
        viewer.addEventListener('error', onError, { once: true });

        timeoutId = setTimeout(() => done(false, 'timeout après 12 secondes'), 12000);

        try {
          viewer.setAttribute('src', modelUrl);
          if (viewer.loaded === true) setTimeout(() => done(true), 0);
        } catch (error) {
          done(false, error?.message || String(error));
        }
      });

      if (token !== currentLoadToken) return false;

      if (!result.ok) {
        setError(
          'Le modèle 3D n’a pas pu être chargé. Appuyez sur « Réessayer ».',
          `Échec de chargement (${reason}) : ${result.detail}`
        );
        return false;
      }

      if (loading) loading.hidden = true;
      clearError();
      await applyArtworkTexture();
      return true;
    };

    const applyPreviewVariant = async (variant, force = false) => {
      if (!variant) return;

      const { dims, frameLabel } = renderConfiguration(variant);
      const widthM = Math.max(0.05, dims.widthCm / 100);
      const heightM = Math.max(0.05, dims.heightCm / 100);
      viewer.setAttribute('scale', `${widthM} ${heightM} 1`);

      if (desktopMode) {
        if (loading) loading.hidden = true;
        if (errorBox) errorBox.hidden = true;
        return;
      }

      if (!modelViewerReady) return;

      const modelUrl = chooseModel(frameLabel);

      if (modelUrl && (force || modelUrl !== lastModelUrl)) {
        lastModelUrl = modelUrl;
        lastAppliedArtwork = '';
        await loadModel(modelUrl, 'changement de configuration');
      } else if (artworkUrl && artworkUrl !== lastAppliedArtwork && viewer.model) {
        await applyArtworkTexture();
      }
    };

    const selectSizeChoice = async (value) => {
      if (sizeIndex < 0) return;
      previewSizeValue = value;
      manualPreview = true;
      const current = activeVariant();
      const next = findVariantForChoice(sizeIndex, value, current);
      previewVariant = next || current;
      await applyPreviewVariant(previewVariant, true);
    };

    const selectFrameChoice = async (value) => {
      if (frameIndex < 0) return;
      // Le cadre du visualiseur est volontairement indépendant des variantes Shopify.
      // Ainsi Noir / Blanc / Beige / Sans cadre restent disponibles même avec beaucoup de variantes.
      previewFrameValue = value;
      manualPreview = true;
      await applyPreviewVariant(activeVariant(), true);
    };

    sizeChoices?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-ah-ar-choice="size"]');
      if (!button) return;
      selectSizeChoice(button.dataset.ahArValue || '');
    });

    frameChoices?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-ah-ar-choice="frame"]');
      if (!button) return;
      selectFrameChoice(button.dataset.ahArValue || '');
    });

    try {
      const params = new URL(window.location.href).searchParams;
      const urlSize = params.get('ah_ar_size');
      const urlFrame = params.get('ah_ar_frame');
      if (urlSize) previewSizeValue = urlSize;
      if (urlFrame) previewFrameValue = urlFrame;
      if (urlSize || urlFrame) manualPreview = true;
    } catch (_) {}

    const setDesktopLayout = () => {
      desktopMode = isLikelyDesktop();

      if (desktopMode) {
        root.classList.add('ah-ar--desktop-transfer');
        if (desktopTransfer) desktopTransfer.hidden = false;
        viewer.hidden = true;
        if (loading) loading.hidden = true;
        if (errorBox) errorBox.hidden = true;
        if (unsupported) unsupported.hidden = true;
      } else {
        root.classList.remove('ah-ar--desktop-transfer');
        if (desktopTransfer) desktopTransfer.hidden = true;
        viewer.hidden = false;
      }
    };

    const ensureMobileViewer = async () => {
      if (desktopMode || modelViewerReady) return modelViewerReady;
      setLoading(true, 'Initialisation de la 3D…');
      modelViewerReady = await ensureModelViewer();

      if (!modelViewerReady) {
        setError(
          'Le moteur 3D n’a pas pu démarrer. Rechargez la page puis réessayez.',
          'Le composant <model-viewer> n’a pas été défini.'
        );
        return false;
      }

      lastModelUrl = '';
      await applyPreviewVariant(activeVariant(), true);
      return true;
    };

    const focusableSelector =
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const open = async () => {
      lastFocused = document.activeElement;
      overlay.hidden = false;
      document.documentElement.classList.add('ah-ar-lock');
      document.body.classList.add('ah-ar-lock');

      previewVariant = storefrontVariant();
      manualPreview = false;
      setDesktopLayout();
      await applyPreviewVariant(previewVariant, true);

      if (!desktopMode) {
        await ensureMobileViewer();
      }

      requestAnimationFrame(() => {
        overlay.querySelector('.ah-ar__close')?.focus();
      });
    };

    const close = () => {
      overlay.hidden = true;
      document.documentElement.classList.remove('ah-ar-lock');
      document.body.classList.remove('ah-ar-lock');
      previewVariant = null;
      manualPreview = false;
      lastFocused?.focus?.();
    };

    openButton.addEventListener('click', open);
    closeButtons.forEach((button) => button.addEventListener('click', close));

    retryButton?.addEventListener('click', async () => {
      if (desktopMode) return;
      const current = activeVariant();
      const opts = Array.isArray(current?.options) ? current.options : [];
      const frameLabel = frameIndex >= 0 ? opts[frameIndex] : 'Cadre noir';
      await loadModel(chooseModel(frameLabel), 'réessai manuel');
    });

    document.addEventListener('keydown', (event) => {
      if (overlay.hidden) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }

      if (event.key !== 'Tab') return;

      const nodes = [...overlay.querySelectorAll(focusableSelector)]
        .filter((el) => el.offsetParent !== null);
      if (!nodes.length) return;

      const first = nodes[0];
      const last = nodes[nodes.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    copyButton?.addEventListener('click', async () => {
      const url = makeMobileUrl(activeVariant());
      try {
        await navigator.clipboard.writeText(url);
        if (copyLabel) copyLabel.textContent = 'Lien copié';
        setTimeout(() => {
          if (copyLabel) copyLabel.textContent = 'Copier le lien de cette fiche';
        }, 1800);
      } catch (_) {
        if (copyLabel) copyLabel.textContent = 'Copiez l’adresse de votre navigateur';
      }
    });

    viewer.addEventListener('ar-status', (event) => {
      const status = event.detail?.status;
      if (status === 'failed' && unsupported && !desktopMode) {
        unsupported.hidden = false;
      }
    });

    const syncFromStorefront = async () => {
      const id = getVariantId(root);
      if (!id || Number(id) === Number(lastStoreVariantId)) return;
      lastStoreVariantId = id;

      if (overlay.hidden || !manualPreview) {
        previewVariant = variantById(id) || variants[0];
        if (!overlay.hidden) await applyPreviewVariant(previewVariant, true);
      }
    };

    const section = document.getElementById(`MainProduct-${root.dataset.sectionId}`) || document;
    section.addEventListener('change', () => setTimeout(syncFromStorefront, 0));
    document.addEventListener('variant:change', () => setTimeout(syncFromStorefront, 0));
    document.addEventListener('product:variant-change', () => setTimeout(syncFromStorefront, 0));

    window.addEventListener('resize', () => {
      if (overlay.hidden) return;
      const wasDesktop = desktopMode;
      setDesktopLayout();
      if (wasDesktop !== desktopMode) {
        applyPreviewVariant(activeVariant(), true);
        if (!desktopMode) ensureMobileViewer();
      }
    });

    artworkRatio = await loadImageRatio(artworkUrl);

    previewVariant = storefrontVariant();
    lastStoreVariantId = previewVariant?.id || null;
    renderConfiguration(previewVariant);

    const variantPoll = window.setInterval(() => {
      if (!document.documentElement.contains(root)) {
        window.clearInterval(variantPoll);
        return;
      }
      syncFromStorefront();
    }, 900);
  }

  const boot = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      init(root).catch((error) => console.error('[Ambient AR] Initialisation impossible', error));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target || document));
})();
